/**
 * BtsGuide: the visual guide of the BTS booklets (/bts, /bts/new, /bts/:uuid), opened by the "?" after each page's
 * title (HelpTitle helpId="bts"; the same guide on the three pages). Only admins open these pages, so every step is
 * theirs; the moderator's part (the confirmed subject variants a booklet takes) is told in the roles.
 * - The steps follow a booklet's life: create it (programme, grade, language and day; its codes box, whose digits
 *   follow the language and the day), the booklet page's map of five steps (BookletSteps: what is done, what to do
 *   now and the link to its card), its subjects, the check, activating (it freezes), the PDFs, the answer keys,
 *   archiving (and activating again). The texts live under help.bts.* and name the buttons and menu items through
 *   their own i18n keys; the legend shows the pages' own status badges.
 * - It follows the screen: under 768px (the pages' breakpoint for the status actions and the subjects' rows, as
 *   OverflowMenu's) "⋯" is framed beside a full-width main button and a subject is moved from its row's "⋯"; under
 *   640px (BookletSteps' breakpoint) the map's steps stand under each other.
 */
import React from "react";
import { useTranslation } from "react-i18next";
import {
  ArchiveBoxIcon,
  ArrowDownIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  ArrowUpIcon,
  ArrowUturnLeftIcon,
  ClockIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  EllipsisHorizontalIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  KeyIcon,
  LockClosedIcon,
  PlayIcon,
  PlusIcon,
  PrinterIcon,
  ShieldCheckIcon,
  TableCellsIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon as CheckCircleSolidIcon } from "@heroicons/react/24/solid";
import { HelpFlow, HelpIntro, HelpLegend, HelpRoles, HelpStep, HelpSteps, HelpText, HelpTips } from "../HelpKit";
import {
  MiniBadge,
  MiniButton,
  MiniCard,
  MiniDialog,
  MiniDivider,
  MiniField,
  MiniIcon,
  MiniMenu,
  MiniRow,
  MiniScreen,
  MiniStack,
  MiniTable,
  MiniTabs,
  MiniText,
} from "../Mini";
import { useMediaQuery } from "../../../pages/Exams/TestBuilder/ui";
import { BookletStatusBadge, FrozenBadge } from "../../../pages/Bts/bts";

// The example booklet: BTS-9 (help.common.example.exam), grade 10, Kazakh, day 1: the scheme's digits 1 and 2 print
// the codes 9101 and 9102.
const GRADE = 10;
const DAY = 1;
const CODES = ["9101", "9102"];

// The map's circles (BookletSteps): the step to do now in blue, the ones ahead white (a done one is a green tick).
const CURRENT = "bg-blue-600 text-white ring-blue-600";
const AHEAD = "bg-white text-slate-400 ring-slate-300";

export default function BtsGuide() {
  const [t] = useTranslation();
  const wide = useMediaQuery("(min-width: 768px)");
  // BookletSteps' own breakpoint: its five steps in a row from 640px, under each other below.
  const roomy = useMediaQuery("(min-width: 640px)");
  const ex = (key) => t(`help.common.example.${key}`);
  // Named in the booklet's own language (a Kazakh one here), with its day, as the create form names it.
  const bookletName = t("bts.create.suggested_name_day", {
    lng: "kk",
    folder: ex("exam"),
    grade: GRADE,
    day: DAY,
    stream: t("bts.languages.kk", { lng: "kk" }),
  });
  const variantOption = t("bts.sections.variant_option", { name: t("help.bts.example.variant"), count: 20 });
  // "⋯" as the pages draw it: plain from md up, framed on phones.
  const more = <MiniButton variant={wide ? "quiet" : "outline"} icon={EllipsisHorizontalIcon} />;
  // A blocking issue of the check, and its link to the subject's variant in the bank.
  const issueLine = `${ex("subject")} — ${t("bts.issues.variant_not_confirmed")}`;
  const openVariant = (
    <MiniButton variant="link" icon={ArrowTopRightOnSquareIcon} mark="1">
      {t("bts.check.open_variant")}
    </MiniButton>
  );

  // The booklet page's map just after the booklet is made: its settings done, its subjects to do now, the rest ahead;
  // under it the line saying what to do now and the link to that card.
  const mapSteps = [
    { key: "settings", state: "done" },
    { key: "subjects", state: "current" },
    { key: "check", state: "ahead" },
    { key: "activate", state: "ahead" },
    { key: "print", state: "ahead" },
  ];
  const mapStep = ({ key, state }, index) => (
    <MiniRow key={key} className="flex-nowrap">
      {state === "done" ? (
        <MiniIcon icon={CheckCircleSolidIcon} tone="green" />
      ) : (
        <MiniBadge className={state === "current" ? CURRENT : AHEAD}>{String(index + 1)}</MiniBadge>
      )}
      <MiniText variant={state === "ahead" ? "muted" : "strong"}>{t(`bts.steps.${key}`)}</MiniText>
    </MiniRow>
  );
  const goSubjects = (
    <MiniButton variant="link" icon={ArrowDownIcon} mark>
      {t("bts.steps.go_subjects")}
    </MiniButton>
  );
  const nowLine = `${t("bts.steps.now")} ${t("bts.steps.next_subjects", { save: t("bts.sections.save") })}`;

  const download = (mark) => (
    <MiniButton variant="outline" icon={ArrowDownTrayIcon} mark={mark}>
      {t("buttons.download")}
    </MiniButton>
  );
  const log = (
    <MiniButton variant="quiet" icon={ClockIcon}>
      {t("bts.print.log_button")}
    </MiniButton>
  );
  // A document of a variant: what it is, then its download and its log (in this order on every width).
  const documentRow = (key, kind, mark) => (
    <MiniRow key={key} className="justify-between gap-y-3">
      <MiniRow>
        <MiniIcon icon={kind === "key" ? KeyIcon : DocumentTextIcon} tone={kind === "key" ? "amber" : "blue"} />
        <MiniText variant="strong">{t(`bts.print.kind_${kind}`)}</MiniText>
        {kind === "key" && (
          <MiniBadge tone="amber" icon={LockClosedIcon}>
            {t("bts.print.confidential")}
          </MiniBadge>
        )}
      </MiniRow>
      <MiniRow className="flex-nowrap">
        {download(mark)}
        {log}
      </MiniRow>
    </MiniRow>
  );

  return (
    <>
      <HelpIntro>
        <HelpText i18nKey="help.bts.intro" values={{ exam: ex("exam") }} />
      </HelpIntro>

      <HelpFlow pipeline="bts" current="booklets" />

      <HelpSteps>
        <HelpStep
          title={t("help.bts.steps.create.title")}
          illustration={
            <MiniStack>
              <MiniRow className="justify-between gap-y-3">
                <MiniText variant="title">{t("nav.bts")}</MiniText>
                <MiniButton variant="primary" icon={PlusIcon} mark="1">
                  {t("bts.list.create")}
                </MiniButton>
              </MiniRow>
              <MiniScreen title={t("bts.create.title")}>
                <MiniRow>
                  <MiniField label={t("bts.fields.folder")} value={ex("exam")} select className="flex-1" />
                  <MiniField label={t("bts.fields.grade")} value={t("bts.grade_value", { grade: GRADE })} select className="flex-1" />
                </MiniRow>
                {/* The language and the day, side by side. */}
                <MiniRow className="gap-y-3">
                  <MiniStack tight>
                    <MiniRow>
                      <MiniText variant="muted">{t("bts.fields.language")}</MiniText>
                    </MiniRow>
                    <MiniTabs items={[t("bts.language_names.kk"), t("bts.language_names.ru")]} current={0} />
                  </MiniStack>
                  <MiniStack tight>
                    <MiniRow>
                      <MiniText variant="muted">{t("bts.fields.day")}</MiniText>
                    </MiniRow>
                    <MiniTabs items={[t("bts.create.day_n", { n: 1 }), t("bts.create.day_n", { n: 2 })]} current={DAY - 1} />
                  </MiniStack>
                </MiniRow>
                {/* The subjects of that grade and language with a confirmed variant. */}
                <MiniCard tone="slate" icon={InformationCircleIcon}>
                  <MiniText variant="strong">{t("bts.create.sources_ready", { count: 1, total: 2 })}</MiniText>
                  <MiniRow>
                    <MiniBadge tone="green" icon={CheckCircleIcon}>
                      {ex("subject")}
                    </MiniBadge>
                    <MiniBadge>{ex("subject_2")}</MiniBadge>
                  </MiniRow>
                </MiniCard>
                {/* The codes the scheme gives the language and the day; their digits change by hand under the link. */}
                <MiniCard tone="slate">
                  <MiniRow className="justify-between gap-y-3">
                    <MiniRow>
                      <MiniText variant="strong">{t("bts.create.codes_title")}</MiniText>
                      {CODES.map((code) => (
                        <MiniBadge key={code}>{code}</MiniBadge>
                      ))}
                    </MiniRow>
                    <MiniButton variant="link" mark="2">
                      {t("bts.create.digits_change")}
                    </MiniButton>
                  </MiniRow>
                </MiniCard>
                <MiniField label={t("bts.fields.name")} value={bookletName} />
                <MiniRow className="justify-end gap-y-3">
                  <MiniButton variant="outline">{t("buttons.cancel")}</MiniButton>
                  <MiniButton variant="primary" mark="3">
                    {t("bts.create.submit")}
                  </MiniButton>
                </MiniRow>
              </MiniScreen>
            </MiniStack>
          }
        >
          <HelpText i18nKey="help.bts.steps.create.text" values={{ create: t("bts.list.create") }} />
          <p>
            <HelpText
              i18nKey="help.bts.steps.create.text_codes"
              values={{
                codes: t("bts.create.codes_title"),
                code: CODES[0],
                change: t("bts.create.digits_change"),
                first: t("bts.fields.round_number"),
                submit: t("bts.create.submit"),
              }}
            />
          </p>
        </HelpStep>

        <HelpStep
          title={t("help.bts.steps.map.title")}
          illustration={
            <MiniScreen title={bookletName}>
              {roomy ? <MiniRow>{mapSteps.map(mapStep)}</MiniRow> : <MiniStack>{mapSteps.map(mapStep)}</MiniStack>}
              {/* What to do now, and the link to its card: at the line's end from md up, under it on phones. */}
              <MiniCard tone="blue" aside={wide ? goSubjects : undefined}>
                {wide ? (
                  nowLine
                ) : (
                  <MiniStack>
                    <MiniStack tight>{nowLine}</MiniStack>
                    <MiniRow>{goSubjects}</MiniRow>
                  </MiniStack>
                )}
              </MiniCard>
            </MiniScreen>
          }
        >
          <HelpText i18nKey="help.bts.steps.map.text" values={{ go: t("bts.steps.go_subjects") }} />
        </HelpStep>

        <HelpStep
          title={t("help.bts.steps.sections.title")}
          illustration={
            <MiniScreen title={t("bts.sections.title")}>
              <MiniCard>
                <MiniStack tight>
                  <MiniRow>
                    <MiniText variant="strong">{`1 · ${ex("subject")}`}</MiniText>
                  </MiniRow>
                  <MiniRow>
                    <MiniText variant="muted">
                      {t("bts.sections.questions", { count: 20 })} · {t("bts.sections.points", { count: 100 })}
                    </MiniText>
                  </MiniRow>
                </MiniStack>
                <MiniRow className="flex-nowrap gap-y-3">
                  <MiniField value={variantOption} select className="flex-1" />
                  {wide && <MiniButton variant="quiet" icon={ArrowUpIcon} mark="2" />}
                  {wide && <MiniButton variant="quiet" icon={ArrowDownIcon} />}
                  {wide ? more : <MiniButton variant="outline" icon={EllipsisHorizontalIcon} mark="2" />}
                </MiniRow>
              </MiniCard>
              <MiniText variant="strong">{t("bts.sections.add_title")}</MiniText>
              <MiniCard>
                <MiniRow className="justify-between gap-y-3">
                  <MiniText variant="strong">{ex("subject_2")}</MiniText>
                  <MiniButton variant="link" icon={PlusIcon} mark="1">
                    {t("buttons.add")}
                  </MiniButton>
                </MiniRow>
              </MiniCard>
              <MiniDivider />
              <MiniRow className="justify-end gap-y-3">
                <MiniButton variant="outline">{t("buttons.cancel")}</MiniButton>
                <MiniButton variant="primary" mark="3">
                  {t("bts.sections.save")}
                </MiniButton>
              </MiniRow>
            </MiniScreen>
          }
        >
          <HelpText
            i18nKey={wide ? "help.bts.steps.sections.text" : "help.bts.steps.sections.text_phone"}
            values={{
              available: t("bts.sections.add_title"),
              add: t("buttons.add"),
              save: t("bts.sections.save"),
              remove: t("bts.sections.remove_short"),
              open: t("bts.sections.open_short"),
            }}
          />
          <p>
            <HelpText i18nKey="help.bts.steps.sections.text_confirm" values={{ variant: t("folders.tab_variant") }} />
          </p>
        </HelpStep>

        <HelpStep
          title={t("help.bts.steps.check.title")}
          illustration={
            <MiniScreen title={t("bts.check.title")}>
              <MiniRow className="justify-between gap-y-3">
                <MiniRow>
                  <MiniIcon icon={XCircleIcon} tone="red" />
                  <MiniText variant="strong">{t("bts.check.blocked", { count: 1 })}</MiniText>
                </MiniRow>
                <MiniButton variant="quiet" icon={ArrowPathIcon} mark="2">
                  {t("bts.check.refresh")}
                </MiniButton>
              </MiniRow>
              {/* Plain text takes the card's colour, as the issues' own; the link at the issue's end from md up, under
                  it on phones (IssueList). */}
              <MiniCard tone="red" icon={XCircleIcon} aside={wide ? openVariant : undefined}>
                {wide ? (
                  issueLine
                ) : (
                  <MiniStack>
                    <MiniStack tight>{issueLine}</MiniStack>
                    <MiniRow>{openVariant}</MiniRow>
                  </MiniStack>
                )}
              </MiniCard>
              <MiniCard tone="amber" icon={ExclamationTriangleIcon}>
                {`${ex("subject_2")} — ${t("bts.issues.option_count", { count: 1 })}`}
              </MiniCard>
            </MiniScreen>
          }
        >
          <HelpText
            i18nKey="help.bts.steps.check.text"
            values={{ check: t("bts.check.title"), open: t("bts.check.open_variant"), refresh: t("bts.check.refresh") }}
          />
        </HelpStep>

        <HelpStep
          title={t("help.bts.steps.activate.title")}
          illustration={
            <MiniStack>
              <MiniRow className="justify-between gap-y-3">
                <MiniRow>
                  <MiniText variant="title">{bookletName}</MiniText>
                  <MiniBadge tone="amber">{t("bts.status.draft")}</MiniBadge>
                </MiniRow>
                <MiniRow className="flex-nowrap">
                  <MiniButton variant="primary" icon={PlayIcon} mark="1">
                    {t("bts.actions.activate")}
                  </MiniButton>
                  {more}
                </MiniRow>
              </MiniRow>
              <MiniDialog
                title={t("bts.status_dialog.activate_title")}
                footer={[
                  <MiniButton key="cancel" variant="outline">
                    {t("buttons.cancel")}
                  </MiniButton>,
                  <MiniButton key="activate" variant="primary" mark="2">
                    {t("bts.actions.activate")}
                  </MiniButton>,
                ]}
              >
                <MiniCard tone="blue" icon={LockClosedIcon}>
                  {t("bts.status_dialog.activate_text")}
                </MiniCard>
              </MiniDialog>
            </MiniStack>
          }
        >
          <HelpText i18nKey="help.bts.steps.activate.text" values={{ activate: t("bts.actions.activate"), frozen: t("bts.frozen") }} />
        </HelpStep>

        <HelpStep
          title={t("help.bts.steps.pdf.title")}
          illustration={
            <MiniScreen title={t("bts.print.title")}>
              <MiniRow className="justify-end gap-y-3">
                <MiniButton variant="primary" icon={PrinterIcon} mark="1">
                  {t("bts.print.create")}
                </MiniButton>
              </MiniRow>
              <MiniCard>
                <MiniText variant="strong">{t("bts.print.variant", { code: CODES[0] })}</MiniText>
                <MiniDivider />
                {documentRow("student", "student", "2")}
                {documentRow("key", "key")}
              </MiniCard>
            </MiniScreen>
          }
        >
          <HelpText i18nKey="help.bts.steps.pdf.text" values={{ create: t("bts.print.create") }} />
          <p>
            <HelpText i18nKey="help.bts.steps.pdf.text_log" values={{ log: t("bts.print.log_button") }} />
          </p>
        </HelpStep>

        <HelpStep
          title={t("help.bts.steps.keys.title")}
          illustration={
            <MiniScreen title={t("bts.answer_keys.title")}>
              <MiniRow className="justify-end gap-y-3">
                <MiniButton variant="quiet" icon={TableCellsIcon} mark="2">
                  {t("bts.answer_keys.table")}
                </MiniButton>
              </MiniRow>
              <MiniRow className="gap-y-3">
                {CODES.map((code, index) => (
                  <MiniButton key={code} variant="outline" icon={KeyIcon} mark={index === 0 ? "1" : undefined}>
                    {`${code} ${t("bts.answer_keys.open")}`}
                  </MiniButton>
                ))}
              </MiniRow>
              <MiniTable
                columns={[
                  { label: t("bts.answer_keys.col_number"), width: "2.75rem" },
                  t("bts.answer_keys.col_answer"),
                  { label: t("bts.answer_keys.col_points"), width: "4rem", align: "right" },
                ]}
                rows={[
                  ["1", "B", "3"],
                  ["2", "D", "5"],
                  ["3", "A", "10"],
                ]}
              />
            </MiniScreen>
          }
        >
          <HelpText i18nKey="help.bts.steps.keys.text" values={{ open: t("bts.answer_keys.open"), table: t("bts.answer_keys.table") }} />
        </HelpStep>

        <HelpStep
          title={t("help.bts.steps.archive.title")}
          illustration={
            <MiniStack>
              <MiniRow className="justify-between gap-y-3">
                <MiniRow>
                  <MiniText variant="title">{bookletName}</MiniText>
                  <MiniBadge tone="green">{t("bts.status.active")}</MiniBadge>
                  <MiniBadge tone="cyan" icon={LockClosedIcon}>
                    {t("bts.frozen")}
                  </MiniBadge>
                </MiniRow>
                <MiniRow className="flex-nowrap">
                  <MiniButton variant={wide ? "quiet" : "outline"} icon={ArchiveBoxIcon} mark="1">
                    {t("bts.actions.archive")}
                  </MiniButton>
                  {more}
                </MiniRow>
              </MiniRow>
              {/* "⋯" open: going back to a draft. */}
              <MiniRow className="justify-end">
                <MiniMenu items={[{ label: t("bts.actions.to_draft"), icon: ArrowUturnLeftIcon, mark: "2" }]} />
              </MiniRow>
            </MiniStack>
          }
        >
          <HelpText i18nKey="help.bts.steps.archive.text" values={{ archive: t("bts.actions.archive"), to_draft: t("bts.actions.to_draft") }} />
          <p>
            <HelpText i18nKey="help.bts.steps.archive.text_reactivate" values={{ reactivate: t("bts.actions.reactivate") }} />
          </p>
        </HelpStep>
      </HelpSteps>

      <HelpLegend
        rows={[
          { key: "draft", badge: <BookletStatusBadge status="draft" />, text: t("help.bts.legend.draft") },
          { key: "active", badge: <BookletStatusBadge status="active" />, text: t("help.bts.legend.active") },
          { key: "archived", badge: <BookletStatusBadge status="archived" />, text: t("help.bts.legend.archived") },
          { key: "frozen", badge: <FrozenBadge />, text: t("bts.frozen_hint") },
        ]}
      />

      <HelpRoles
        rows={[
          { key: "admin", role: t("folders.roles.admin"), Icon: KeyIcon, can: t("help.bts.roles.admin") },
          {
            key: "moderator",
            role: t("folders.roles.moderator"),
            Icon: ShieldCheckIcon,
            can: <HelpText i18nKey="help.bts.roles.moderator" values={{ variant: t("folders.tab_variant") }} />,
          },
        ]}
      />

      <HelpTips
        items={[
          <HelpText key="settings" i18nKey="help.bts.tips.settings" values={{ settings: t("bts.settings.title") }} />,
          <HelpText key="labels" i18nKey="help.bts.tips.labels" values={{ labels: t("bts.labels.title") }} />,
          <HelpText key="refresh" i18nKey="help.bts.tips.refresh" values={{ refresh: t("bts.check.refresh") }} />,
          <HelpText key="delete" i18nKey="help.bts.tips.delete" values={{ delete: t("bts.actions.delete") }} />,
        ]}
      />
    </>
  );
}
